/**
 * Digital Signage Screen 1 — Cennik
 * Same glass-card style as /pl/cennik but BIGGER fonts for 55" screens viewed from 2-3m
 * Uses /api/pricing/ds (shows ALL session slots even hidden)
 * Auto-switches Weekend Sob/Ndz tabs every 10s
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_INTERVAL = 5 * 60 * 1000;
const SLIDER_INTERVAL = 12000;
const WEEKEND_SWITCH = 10000;

// DS-sized options renderer — bigger text for signage
const DSOptions = ({ options }) => (
  <div className="px-8 py-6 flex-1 space-y-6">
    {options.map((option) => (
      <div key={option.option_id}>
        <div className="text-2xl text-white/50 font-semibold mb-3">
          {option.name?.pl || option.name?.en}
        </div>
        <div className="flex items-center gap-5 mb-12">
          <div className="flex-1">
            <div className="text-sm text-white/30 mb-1 font-medium">Bez rezerwacji</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-4xl font-['Syne'] font-bold text-white">{option.price}</span>
              <span className="text-lg text-white/30">zł</span>
            </div>
          </div>
          {option.priceWithReservation != null && (
            <div className="flex-1 p-4 rounded-2xl bg-primary/10 border border-primary/20">
              <div className="text-sm text-primary mb-1 flex items-center gap-1.5 font-semibold">
                <Check className="w-4 h-4" />
                Z rezerwacją
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-4xl font-['Syne'] font-bold text-primary">{option.priceWithReservation}</span>
                <span className="text-lg text-primary/50">zł</span>
              </div>
            </div>
          )}
        </div>
        {option.sessionSlots && option.sessionSlots.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {option.sessionSlots.map((slot, si) => (
              <span key={si} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-mono font-semibold bg-primary/15 text-primary border border-primary/25">
                <Clock className="w-4 h-4" />
                {slot}
              </span>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

const DSScreen1 = () => {
  const [pricing, setPricing] = useState(null);
  const [sliders, setSliders] = useState([]);
  const [slideIdx, setSlideIdx] = useState(0);
  const [slideFade, setSlideFade] = useState(true);
  const [weekendDay, setWeekendDay] = useState('sat');

  const fetchData = async () => {
    try {
      const [p, s] = await Promise.all([
        axios.get(`${API}/api/pricing/ds`),
        axios.get(`${API}/api/admin/slider`)
      ]);
      setPricing(p.data);
      setSliders(s.data || []);
    } catch (e) { console.error('DS1:', e); }
  };

  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_INTERVAL); return () => clearInterval(i); }, []);
  useEffect(() => {
    if (sliders.length <= 1) return;
    const i = setInterval(() => { setSlideFade(false); setTimeout(() => { setSlideIdx(p => (p + 1) % sliders.length); setSlideFade(true); }, 500); }, SLIDER_INTERVAL);
    return () => clearInterval(i);
  }, [sliders.length]);
  useEffect(() => {
    if (!pricing) return;
    const s = pricing.schedules || [];
    if (!s.some(x => x.schedule_id === 'sat') || !s.some(x => x.schedule_id === 'sun')) return;
    const i = setInterval(() => setWeekendDay(d => d === 'sat' ? 'sun' : 'sat'), WEEKEND_SWITCH);
    return () => clearInterval(i);
  }, [pricing]);

  if (!pricing) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
      <div className="w-16 h-16 border-4 border-primary/20 rounded-full animate-spin border-t-primary" />
    </div>
  );

  const all = pricing.schedules || [];
  const sat = all.find(s => s.schedule_id === 'sat');
  const sun = all.find(s => s.schedule_id === 'sun');
  const hasSplit = sat && sun;
  const regular = hasSplit ? all.filter(s => s.schedule_id !== 'sat' && s.schedule_id !== 'sun') : all;
  const weekendSchedule = hasSplit ? (weekendDay === 'sat' ? sat : sun) : null;
  const hasSlider = sliders.length > 0;
  const slide = sliders[slideIdx];

  const renderCard = (schedule) => {
    const mainOpts = (schedule.options || []).filter(o => o.type !== 'lasthour');
    const lastHour = (schedule.options || []).find(o => o.type === 'lasthour');
    return (
      <div className="glass-card h-full flex flex-col" key={schedule.schedule_id}>
        <div className="px-8 py-5 border-b border-white/10">
          <h3 className="text-3xl font-['Syne'] font-bold text-white">
            {schedule.name?.pl || schedule.name?.en}
          </h3>
        </div>
        <DSOptions options={mainOpts} />
        {lastHour && (
          <div className="px-8 pb-6 pt-0 mt-auto">
            <div className="flex items-center justify-between py-3 px-5 rounded-2xl bg-white/5">
              <span className="text-base text-white/30">{lastHour.name?.pl}</span>
              <span className="text-xl font-['Syne'] font-bold text-white">{lastHour.price} zł</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderWeekend = () => {
    const schedule = weekendSchedule;
    const mainOpts = (schedule.options || []).filter(o => o.type !== 'lasthour');
    const lastHour = (schedule.options || []).find(o => o.type === 'lasthour');
    return (
      <div className="glass-card h-full flex flex-col" key="weekend">
        <div className="px-8 py-5 border-b border-white/10">
          <div className="flex items-center gap-4">
            <h3 className="text-3xl font-['Syne'] font-bold text-white shrink-0">Weekend</h3>
            <div className="flex ml-auto p-1 rounded-2xl bg-white/5 border border-white/10">
              <span className={cn(
                "px-5 py-2.5 rounded-xl text-base font-bold transition-all",
                weekendDay === 'sat' ? "bg-primary text-white shadow-lg shadow-primary/30" : "text-white/30"
              )}>
                {sat?.name?.pl || 'Sobota'}
              </span>
              <span className={cn(
                "px-5 py-2.5 rounded-xl text-base font-bold transition-all",
                weekendDay === 'sun' ? "bg-primary text-white shadow-lg shadow-primary/30" : "text-white/30"
              )}>
                {sun?.name?.pl || 'Niedziela'}
              </span>
            </div>
          </div>
        </div>
        <DSOptions options={mainOpts} />
        {lastHour && (
          <div className="px-8 pb-6 pt-0 mt-auto">
            <div className="flex items-center justify-between py-3 px-5 rounded-2xl bg-white/5">
              <span className="text-base text-white/30">{lastHour.name?.pl}</span>
              <span className="text-xl font-['Syne'] font-bold text-white">{lastHour.price} zł</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="w-screen h-screen overflow-hidden flex flex-col p-6 gap-4"
      style={{ background: '#050505' }}
    >
      <div className="flex-1 grid grid-cols-3 gap-6 min-h-0">
        {regular.map(sch => renderCard(sch))}
        {hasSplit && renderWeekend()}
      </div>

      {hasSlider && slide && (
        <div
          className="rounded-2xl overflow-hidden relative flex-shrink-0 border border-white/5"
          style={{ height: 190, opacity: slideFade ? 1 : 0, transition: 'opacity 0.5s ease' }}
        >
          {slide.imageUrl && (
            <img
              src={slide.imageUrl.startsWith('/') ? `${API}${slide.imageUrl}` : slide.imageUrl}
              alt="" className="absolute inset-0 w-full h-full object-cover object-right"
            />
          )}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, #050505 0%, #050505 20%, rgba(5,5,5,0.88) 45%, rgba(5,5,5,0.3) 72%, transparent 100%)' }} />
          <div className="relative z-10 h-full flex flex-col justify-center px-12 max-w-[45%]">
            <h3 className="text-3xl font-['Syne'] font-bold text-white leading-tight">
              {slide.title?.pl || slide.title?.en}
            </h3>
            {slide.subtitle?.pl && (
              <p className="text-base text-white/40 mt-2">{slide.subtitle.pl}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DSScreen1;
